import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Check, Pencil } from 'lucide-react'
import { useUpdateThreadRecipients } from '@/features/communications/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatApiErrorMessage } from '@/lib/api-error'
import type { CommunicationThread } from '@/features/communications/types'
import { parseCcString } from './formatting'
import { ComposeInput } from '@/components/compose-field'

export function EditRecipientsDialog({
  thread,
  open,
  onOpenChange,
}: {
  thread: CommunicationThread
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {open && <EditRecipientsForm thread={thread} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

export function RecipientSummary({
  to,
  ccEmails,
  onEdit,
  editTitle,
  actions,
}: {
  to: string | null | undefined
  ccEmails: string[]
  onEdit?: () => void
  editTitle: string
  /** More small icon buttons beside Edit, e.g. closing the thread. */
  actions?: ReactNode
}) {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="mt-1 min-w-0 flex-1 space-y-0.5 text-xs text-muted-foreground">
        {/* Long lines wrap between addresses; a single address too long for the line breaks. */}
        <p className="break-words">
          <span className="font-medium text-foreground/80">To:</span> {to}
        </p>
        <p className="break-words">
          <span className="font-medium text-foreground/80">CC:</span>{' '}
          {ccEmails.length > 0 ? ccEmails.join(', ') : <span className="italic">none</span>}
        </p>
      </div>
      {(onEdit || actions) && (
        <div className="flex shrink-0 items-center gap-0.5">
          {onEdit && (
            <Button
              size="icon-xs"
              variant="ghost"
              className="text-muted-foreground"
              onClick={onEdit}
              title={editTitle}
              aria-label={editTitle}
            >
              <Pencil />
            </Button>
          )}
          {actions}
        </div>
      )}
    </div>
  )
}

function EditRecipientsForm({
  thread,
  onClose,
}: {
  thread: CommunicationThread
  onClose: () => void
}) {
  const [recipientEmail, setRecipientEmail] = useState(thread.recipient_email ?? '')
  const [recipientName, setRecipientName] = useState(thread.recipient_name ?? '')
  const [ccEmails, setCcEmails] = useState(() => parseCcString(thread.cc_emails))
  const mutation = useUpdateThreadRecipients()

  const handleSubmit = () => {
    const cc = ccEmails
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    mutation.mutate(
      {
        threadUuid: thread.uuid ?? '',
        recipient_email: recipientEmail.trim() || undefined,
        recipient_name: recipientName.trim() || null,
        cc_emails: cc,
      },
      {
        onSuccess: () => {
          toast.success('Recipients updated')
          onClose()
        },
        onError: (err) => toast.error(formatApiErrorMessage('Update failed', err)),
      },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit thread recipients</DialogTitle>
        <DialogDescription>
          Changes apply to every future reply in this thread. Past messages are unaffected.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">To</label>
          <ComposeInput
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="vendor@example.com"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">To name (optional)</label>
          <ComposeInput
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Jane Smith"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">CC (comma-separated)</label>
          <ComposeInput
            value={ccEmails}
            onChange={(e) => setCcEmails(e.target.value)}
            placeholder="cc1@example.com, cc2@example.com"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={mutation.isPending}
          loadingText="Saving..."
          onClick={handleSubmit}
          disabled={!recipientEmail.trim()}
        >
          <Check className="mr-1 h-3 w-3" /> Save
        </LoadingButton>
      </DialogFooter>
    </>
  )
}

export type StaffSuggestion = { name: string | null; email: string }

export function StaffEmailField({
  label,
  value,
  onChange,
  staff,
  placeholder,
  autoFocus,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  staff: StaffSuggestion[]
  placeholder?: string
  autoFocus?: boolean
}) {
  const [focused, setFocused] = useState(false)
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(blurTimeout.current), [])
  const inputId = useId()
  const trimmed = value.trim().toLowerCase()
  const lastTokenStart = value.lastIndexOf(',') + 1
  const lastToken = value.slice(lastTokenStart).trim().toLowerCase()
  const tokenForMatch = lastToken || trimmed
  const matches = tokenForMatch
    ? staff.filter(
        (s) =>
          s.email.toLowerCase().includes(tokenForMatch) ||
          (s.name && s.name.toLowerCase().includes(tokenForMatch)),
      )
    : staff
  const exactMatch = matches.some((s) => s.email.toLowerCase() === tokenForMatch)
  const showSuggestions = focused && matches.length > 0 && !exactMatch

  const replaceLastToken = (email: string) => {
    if (lastTokenStart > 0) {
      const prefix = value.slice(0, lastTokenStart).replace(/,?\s*$/, ', ')
      onChange(`${prefix}${email}`)
    } else {
      onChange(email)
    }
    setFocused(false)
  }

  return (
    <div className="relative">
      <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <ComposeInput
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        // Delay so click on suggestion fires before blur clears the list.
        onBlur={() => {
          clearTimeout(blurTimeout.current)
          blurTimeout.current = setTimeout(() => setFocused(false), 150)
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {showSuggestions && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
          {matches.slice(0, 8).map((s) => (
            <button
              key={s.email}
              type="button"
              className="flex w-full flex-col items-start rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onMouseDown={(e) => {
                e.preventDefault()
                replaceLastToken(s.email)
              }}
            >
              <span className="font-medium">{s.name || s.email}</span>
              {s.name && <span className="text-xs text-muted-foreground">{s.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
